import { useStore } from "@tanstack/react-form";
import dayjs from "dayjs";
import { useEffect, useRef, useState } from "react";
import { ChevronDown as ChevronDownIcon } from "@tamagui/lucide-icons";
import { InsuranceCheckRequirement } from "@medbillai/generated-graphql-types";
import { Body1, Body3, Button, FieldShell, Input, Interface2, isWeb, Modal, type ModalFooterProps, Subtitle1, YStack } from "@medbillai/ui";
import { Flag } from "@medbillai/utils/flag";
import { analyticsService } from "../../../lib/analytics";
import { useBoolVariation } from "../../../lib/flag";
import { visibleFieldError } from "../../../lib/form-engine";
import { MemberIdInput } from "../../onboarding-v3/MemberId";
import { TermsCheckbox } from "../../onboarding-v3/Profile";
import { PressableStack } from "../PressableStack";
import { PayerSearch } from "../setup/PayerSearch";
import { ConfirmedField } from "./ConfirmedField";
import { IdentityFields, useIdentityForm } from "./IdentityForm";
import { PayerMark } from "./PlanHeader";
import type { Insurer, PlanSetup, SheetStart } from "./usePlanSetup";
type View = "form" | "picking" | "naming";

/** The sheet's footer, where a step pins its buttons. */
type PinFooter = (footer?: ModalFooterProps) => void;
export const PICKER_TITLE = "Choose your insurer";

/**
 * Coverage's one setup form, on one screen: details, insurer, member ID,
 * terms, then the plan check. In the sheet the insurer picker covers the
 * form; on the card it is a sheet of its own.
 */
export function PlanSetupForm({
  setup,
  missing = [],
  start = "form",
  inSheet = false,
  onPickingChange,
  pinFooter
}: {
  setup: PlanSetup;
  missing?: InsuranceCheckRequirement[];
  start?: SheetStart;
  inSheet?: boolean;
  /** The sheet titles the picker while it covers the form. */
  onPickingChange?: (picking: boolean) => void;
  pinFooter?: PinFooter;
}) {
  useEffect(() => {
    if (setup.checking) {
      analyticsService.trackEvent("CoverageSetupStepViewed", {
        step: "checking"
      });
    }
  }, [setup.checking]);
  if (!setup.loaded && !setup.checking) {
    return null;
  }
  return <YStack data-sentry-element="YStack" data-sentry-component="PlanSetupForm" data-sentry-source-file="PlanSetupForm.tsx">
      {setup.checking ? <SetupChecking detail={askingDetail(setup.insurer?.carrier ?? "")} /> : null}
      {/* Hidden through the check, so a failed save keeps what was typed. */}
      {setup.loaded ? <YStack display={setup.checking ? "none" : "flex"}>
          <SetupFields setup={setup} missing={missing} start={start} inSheet={inSheet} onPickingChange={onPickingChange} pinFooter={pinFooter} />
        </YStack> : null}
    </YStack>;
}

/**
 * The form itself, mounted once the profile loads so which details it asks
 * for, and their starting values, hold still while they're typed.
 */
function SetupFields({
  setup,
  missing,
  start,
  inSheet,
  onPickingChange,
  pinFooter
}: {
  setup: PlanSetup;
  missing: InsuranceCheckRequirement[];
  start: SheetStart;
  inSheet: boolean;
  onPickingChange?: (picking: boolean) => void;
  pinFooter?: PinFooter;
}) {
  const [view, setView] = useState<View>("form");
  useEffect(() => {
    onPickingChange?.(view !== "form");
  }, [view, onPickingChange]);
  const [first] = missing;
  const [askName] = useState(start === "profile" || setup.name === null);
  const [editingDob, setEditingDob] = useState(askName || setup.dateOfBirth === null || first === InsuranceCheckRequirement.NAME || first === InsuranceCheckRequirement.DATE_OF_BIRTH);
  const [focusDob, setFocusDob] = useState(false);
  const identity = useIdentityForm({
    askName,
    askInsurer: true,
    askMemberId: useBoolVariation(Flag.INSURANCE_MEMBER_ID_ASK, false),
    needsTerms: setup.needsTerms,
    initialName: setup.name,
    initialDob: setup.dateOfBirth,
    initialInsurer: setup.insurer,
    onDone: ({
      insurer,
      ...details
    }) => {
      if (insurer) {
        void setup.check({
          ...details,
          insurer
        }).then(sent => {
          if (sent && details.memberId !== undefined) {
            memberIdEntered();
          }
        });
      }
    }
  });
  const {
    form
  } = identity;
  const insurer = useStore(form.store, state => state.values.insurer);
  const asksMemberId = useStore(form.store, state => identity.memberIdAsked(state.values));
  const showTerms = useStore(form.store, state => identity.termsAsked(state.values));
  const memberIdEntered = useMemberIdTracking(asksMemberId ? insurer?.payerId ?? null : null);
  usePinnedFooter(pinFooter, view === "form" ? {
    primaryButtonProps: {
      children: "Check my plan",
      variant: "brand",
      onPress: identity.submit
    }
  } : undefined);
  const step = editingDob ? "profile" : "insurer";
  useEffect(() => {
    analyticsService.trackEvent("CoverageSetupStepViewed", {
      step
    });
  }, [step]);
  const openPicker = () => {
    if (setup.saving) {
      return;
    }
    analyticsService.trackEvent("CoverageAddInsuranceTapped", {
      source: "setup"
    });
    setView("picking");
  };
  const uninsured = () => {
    setView("form");
    setup.uninsured();
  };
  const pick = (picked: Insurer) => {
    if (picked.payerId !== insurer?.payerId) {
      form.setFieldValue("memberId", "");
    }
    form.setFieldValue("insurer", picked);
    setView("form");
  };
  const insurerStep = <InsurerStep naming={view === "naming"} onPick={pick} onNotListed={() => setView("naming")} onBack={() => setView("picking")} onUninsured={inSheet ? undefined : uninsured} pinFooter={pinFooter} />;
  const picking = inSheet && view !== "form";
  return <YStack data-sentry-element="YStack" data-sentry-component="SetupFields" data-sentry-source-file="PlanSetupForm.tsx">
      {/* Hidden, not unmounted, so details typed before picking survive. */}
      <YStack display={picking ? "none" : "flex"} data-sentry-element="YStack" data-sentry-source-file="PlanSetupForm.tsx">
        {editingDob ? <YStack marginTop="$sp1.5" gap="$sp1">
            <IdentityFields identity={identity} focusDob={focusDob} />
          </YStack> : <YStack marginTop="$sp1.5">
            <ConfirmedField label="Date of birth" value={dayjs(setup.dateOfBirth ?? "").format("MMM D, YYYY")} editLabel="Edit date of birth" onEdit={() => {
          setFocusDob(true);
          setEditingDob(true);
        }} />
          </YStack>}
        <form.Field name="insurer" data-sentry-element="form.Field" data-sentry-source-file="PlanSetupForm.tsx">
          {field => <InsurerField insurer={field.state.value} error={visibleFieldError(field.state.meta)} onPress={openPicker} />}
        </form.Field>
        {asksMemberId ? <YStack marginTop="$sp1.5">
            <form.Field name="memberId">
              {field => <MemberIdInput value={field.state.value} onChangeText={field.handleChange} rejected={visibleFieldError(field.state.meta) !== undefined} />}
            </form.Field>
          </YStack> : null}
        {showTerms ? <YStack marginTop="$sp2">
            <form.Field name="termsAccepted">
              {field => <TermsCheckbox checked={field.state.value} meta={field.state.meta} onCheckedChange={field.handleChange} />}
            </form.Field>
          </YStack> : null}
        {pinFooter ? null : <Button variant="brand" size="large" marginTop="$sp2" onPress={identity.submit}>
            Check my plan
          </Button>}
        {setup.failed ? <Body3 color="$textNegative" role="alert" marginTop="$sp1">
            Something went wrong, please try again.
          </Body3> : null}
        {/* First-time setup only: anywhere else the user has said they're insured, or uninsured already. */}
        {inSheet ? null : <UninsuredLink disabled={setup.saving} onPress={uninsured} />}
      </YStack>
      {picking ? insurerStep : null}
      {inSheet ? null : <Modal open={view !== "form"} onOpenChange={open => setView(open ? "picking" : "form")} headerProps={{
      title: PICKER_TITLE,
      variant: "titleLeft",
      // A full-screen web dialog needs its close button.
      hideCloseButton: !isWeb
    }} size="small" snapPoints={[0.9]}>
          {insurerStep}
        </Modal>}
    </YStack>;
}

/** The insurer picker, or naming an insurer the catalog lacks. */
function InsurerStep({
  naming,
  onPick,
  onNotListed,
  onBack,
  onUninsured,
  pinFooter
}: {
  naming: boolean;
  onPick: (insurer: Insurer) => void;
  onNotListed: () => void;
  onBack: () => void;
  onUninsured?: () => void;
  pinFooter?: PinFooter;
}) {
  return naming ? <NamingStep onDone={carrier => onPick({
    carrier,
    payerId: null,
    logoUrl: null,
    memberIdRequired: false
  })} onBack={onBack} pinFooter={pinFooter} data-sentry-element="NamingStep" data-sentry-component="InsurerStep" data-sentry-source-file="PlanSetupForm.tsx" /> : <YStack flex={1} gap="$sp1" paddingBottom="$sp2" marginHorizontal="$-sp1" data-sentry-element="YStack" data-sentry-component="InsurerStep" data-sentry-source-file="PlanSetupForm.tsx">
      <PayerSearch onPick={onPick} onNotListed={onNotListed} data-sentry-element="PayerSearch" data-sentry-source-file="PlanSetupForm.tsx" />
      {onUninsured ? <UninsuredLink onPress={onUninsured} /> : null}
    </YStack>;
}
function NamingStep({
  onDone,
  onBack,
  pinFooter
}: {
  onDone: (carrier: string) => void;
  onBack: () => void;
  pinFooter?: PinFooter;
}) {
  const [name, setName] = useState("");
  const [empty, setEmpty] = useState(false);
  const carrier = name.trim();
  const done = () => carrier === "" ? setEmpty(true) : onDone(carrier);
  usePinnedFooter(pinFooter, {
    primaryButtonProps: {
      testID: "select-insurance.carrier-name.continue",
      children: "Continue",
      variant: "brand",
      onPress: done
    },
    tertiaryButtonProps: {
      children: "Back to search",
      onPress: onBack
    }
  });
  return <YStack gap="$sp1.5" paddingBottom="$sp2" data-sentry-element="YStack" data-sentry-component="NamingStep" data-sentry-source-file="PlanSetupForm.tsx">
      <Body3 color="$textSubtle" data-sentry-element="Body3" data-sentry-source-file="PlanSetupForm.tsx">
        Enter it as it appears on your insurance card.
      </Body3>
      <Input testID="select-insurance.carrier-name" label="Insurance name" value={name} onChangeText={value => {
      setName(value);
      setEmpty(false);
    }} autoFocus autoCorrect={false} validation={empty ? "error" : "none"} helperText={empty ? "Enter your insurance name" : undefined} data-sentry-element="Input" data-sentry-source-file="PlanSetupForm.tsx" />
      {pinFooter ? null : <>
          <Button testID="select-insurance.carrier-name.continue" variant="brand" onPress={done}>
            Continue
          </Button>
          <Button variant="tertiary" onPress={onBack}>
            Back to search
          </Button>
        </>}
    </YStack>;
}

/**
 * Pins a step's buttons to the sheet's footer while it shows. Presses reach
 * the step's latest handlers, so the footer is only re-pinned when a label
 * changes, not on every render.
 */
function usePinnedFooter(pin: PinFooter | undefined, footer: ModalFooterProps | undefined) {
  const latest = useRef(footer);
  useEffect(() => {
    latest.current = footer;
  });
  const primary = footer?.primaryButtonProps.children;
  const tertiary = footer?.tertiaryButtonProps?.children;
  useEffect(() => {
    const current = latest.current;
    if (!pin || !current) {
      return;
    }
    pin({
      primaryButtonProps: {
        ...current.primaryButtonProps,
        children: primary,
        onPress: event => latest.current?.primaryButtonProps.onPress?.(event)
      },
      tertiaryButtonProps: current.tertiaryButtonProps ? {
        ...current.tertiaryButtonProps,
        children: tertiary,
        onPress: event => latest.current?.tertiaryButtonProps?.onPress?.(event)
      } : undefined
    });
    return () => pin(undefined);
  }, [pin, primary, tertiary]);
}
function useMemberIdTracking(payerId: string | null) {
  const entered = useRef(false);
  useEffect(() => {
    if (!payerId) {
      return;
    }
    entered.current = false;
    analyticsService.trackEvent("CoverageSetupMemberIdShown", {
      payerId
    });
    return () => {
      if (!entered.current) {
        analyticsService.trackEvent("CoverageSetupMemberIdResolved", {
          payerId,
          outcome: "left"
        });
      }
    };
  }, [payerId]);
  return () => {
    if (payerId && !entered.current) {
      entered.current = true;
      analyticsService.trackEvent("CoverageSetupMemberIdResolved", {
        payerId,
        outcome: "entered"
      });
    }
  };
}

/** Muted, unlike the DS tertiary button. */
function UninsuredLink({
  disabled = false,
  onPress
}: {
  disabled?: boolean;
  onPress: () => void;
}) {
  return <PressableStack disabled={disabled} alignItems="center" justifyContent="center" minHeight="$si5" marginTop="$sp1.5" marginBottom="$sp1.5" opacity={disabled ? 0.5 : 1} onPress={onPress} data-sentry-element="PressableStack" data-sentry-component="UninsuredLink" data-sentry-source-file="PlanSetupForm.tsx">
      <Interface2 color="$textSubtle" data-sentry-element="Interface2" data-sentry-source-file="PlanSetupForm.tsx">I’m uninsured</Interface2>
    </PressableStack>;
}
function InsurerField({
  insurer,
  error,
  onPress
}: {
  insurer: Insurer | null;
  error: string | undefined;
  onPress: () => void;
}) {
  return <PressableStack aria-label={insurer ? `Insurer, ${insurer.carrier}` : "Insurer"} onPress={onPress} marginTop="$sp1.5" data-sentry-element="PressableStack" data-sentry-component="InsurerField" data-sentry-source-file="PlanSetupForm.tsx">
      <FieldShell label="Insurer" labelFloated={insurer !== null} required validation={error ? "error" : "none"} helperText={error} left={insurer ? <PayerMark name={insurer.carrier} logoUrl={insurer.logoUrl} size="$si3" /> : undefined} right={ChevronDownIcon} pressable data-sentry-element="FieldShell" data-sentry-source-file="PlanSetupForm.tsx">
        {/* An empty field still holds a line, so the resting label centers as an empty Input's does. */}
        <Body1 numberOfLines={1} color="$textDefault" data-sentry-element="Body1" data-sentry-source-file="PlanSetupForm.tsx">
          {insurer?.carrier ?? "\u00a0"}
        </Body1>
      </FieldShell>
    </PressableStack>;
}
export const askingDetail = (insurer: string) => `Asking ${insurer} for your deductible and out-of-pocket max.`;
export function SetupChecking({
  title = "Finding your plan…",
  detail
}: {
  title?: string;
  detail: string;
}) {
  return <YStack marginTop="$sp1.5" aria-busy data-sentry-element="YStack" data-sentry-component="SetupChecking" data-sentry-source-file="PlanSetupForm.tsx">
      <Subtitle1 color="$textDefault" data-sentry-element="Subtitle1" data-sentry-source-file="PlanSetupForm.tsx">{title}</Subtitle1>
      <Body3 color="$textSubtle" marginTop="$sp0.5" marginBottom="$sp1" data-sentry-element="Body3" data-sentry-source-file="PlanSetupForm.tsx">
        {detail}
      </Body3>
      <CheckingTrack data-sentry-element="CheckingTrack" data-sentry-source-file="PlanSetupForm.tsx" />
    </YStack>;
}
function CheckingTrack() {
  const [width, setWidth] = useState(0);
  const [far, setFar] = useState(false);
  const timer = useRef<ReturnType<typeof setInterval> | null>(null);
  useEffect(() => {
    timer.current = setInterval(() => setFar(f => !f), SWEEP_MS);
    return () => {
      if (timer.current) {
        clearInterval(timer.current);
      }
    };
  }, []);
  return <YStack height="$si0.75" borderRadius="$brFull" backgroundColor="$surface0" overflow="hidden" onLayout={e => setWidth(e.nativeEvent.layout.width)} data-sentry-element="YStack" data-sentry-component="CheckingTrack" data-sentry-source-file="PlanSetupForm.tsx">
      <YStack position="absolute" top={0} bottom={0} left={0} width="40%" borderRadius="$brFull" backgroundColor="$bgBrandIntense" transition="expand" x={far ? width * 0.6 : 0} data-sentry-element="YStack" data-sentry-source-file="PlanSetupForm.tsx" />
    </YStack>;
}
const SWEEP_MS = 1_400;